/**
 * Styling part of a text, not all of it (DERP-307).
 *
 * The text stays a plain string — `label` on a header, `content` on a table
 * cell — so everything that reads it as text (the missing-field list, search,
 * the cell clipboard, a template authored before this existed) keeps working
 * untouched. Beside it sits a list of **marks**: character ranges, each with the
 * styles that differ from the element's own. No marks is the whole of every
 * template authored so far, and draws exactly as it always did.
 *
 * A mark's value WINS over the element's style, and an absent key inherits it.
 * `bold: false` is therefore a real instruction — "these words are not bold in
 * a bold heading" — and not the same as leaving `bold` out.
 *
 * Marks are kept normalised: sorted, non-overlapping, never empty, and adjacent
 * ranges with the same styles merged. Every function here returns that shape,
 * so two marks lists that draw the same are the same list.
 *
 * Alignment belongs to a LINE, not a word, but it is stored the same way: on
 * the characters, so it follows its text through every edit with no machinery
 * of its own. A line takes the alignment of its LAST character — the `\n` that
 * ends it, or the final character of the text — and every function here
 * spreads that over the whole line (`unifyLineAligns`), the way a word
 * processor keeps a paragraph's formatting in its paragraph mark. So joining
 * two lines keeps the second one's alignment, and Enter carries the line's
 * alignment onto the new one. No line alignment is every template authored so
 * far, and the element's own alignment applies to all of its text.
 */

export type TextAlign = 'left' | 'center' | 'right' | 'justify';

export interface TextMarkStyle {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  color?: string;
  font_size?: number;
  /** The line's alignment — see the module comment. */
  align?: TextAlign;
}

export interface TextMark extends TextMarkStyle {
  /** Inclusive start offset into the plain text. */
  start: number;
  /** Exclusive end offset. */
  end: number;
}

export interface RichText {
  text: string;
  marks?: TextMark[];
}

/** One run of text that draws in one style, in order. */
export interface TextSegment {
  text: string;
  style?: TextMarkStyle;
}

/** One line of text, ending with its own `\n` when it has one. */
export interface TextLine {
  /** `undefined` follows the element's alignment. */
  align?: TextAlign;
  segments: TextSegment[];
}

/** Every style a text has, resolved: how the toolbar reads it. */
export interface TextFormat {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  color: string;
  font_size: number;
  align: TextAlign;
}

const STYLE_KEYS = ['bold', 'italic', 'underline', 'color', 'font_size', 'align'] as const;

function cleanStyle(s: TextMarkStyle | undefined): TextMarkStyle | undefined {
  if (!s) return undefined;
  const out: TextMarkStyle = {};
  for (const k of STYLE_KEYS) {
    if (s[k] !== undefined) (out as Record<string, unknown>)[k] = s[k];
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function sameStyle(a: TextMarkStyle | undefined, b: TextMarkStyle | undefined): boolean {
  return STYLE_KEYS.every((k) => a?.[k] === b?.[k]);
}

/** The style of each character, `undefined` where it has none of its own. */
function toChars(text: string, marks: TextMark[] | undefined): (TextMarkStyle | undefined)[] {
  const chars: (TextMarkStyle | undefined)[] = Array.from({length: text.length}, () => undefined);
  for (const m of marks ?? []) {
    const style = cleanStyle(m);
    const end = Math.min(m.end, text.length);
    for (let i = Math.max(0, m.start); i < end; i++) chars[i] = style;
  }
  return chars;
}

/** The [start, end) of each line, its `\n` included. */
function lineSpans(text: string): [number, number][] {
  const spans: [number, number][] = [];
  for (let start = 0; start < text.length; ) {
    const nl = text.indexOf('\n', start);
    const end = nl === -1 ? text.length : nl + 1;
    spans.push([start, end]);
    start = end;
  }
  return spans;
}

/** Each line given the alignment its last character carries, in place. */
function unifyLineAligns(text: string, chars: (TextMarkStyle | undefined)[]): void {
  for (const [start, end] of lineSpans(text)) {
    const align = chars[end - 1]?.align;
    for (let i = start; i < end; i++) {
      if (chars[i]?.align !== align) chars[i] = cleanStyle({...chars[i], align});
    }
  }
}

function fromChars(text: string, chars: (TextMarkStyle | undefined)[]): TextMark[] {
  unifyLineAligns(text, chars);
  const marks: TextMark[] = [];
  let i = 0;
  while (i < chars.length) {
    const style = chars[i];
    let j = i + 1;
    while (j < chars.length && sameStyle(chars[j], style)) j++;
    if (style) marks.push({start: i, end: j, ...style});
    i = j;
  }
  return marks;
}

/** `marks` normalised against `text`: clamped, merged, and empty ones dropped. */
export function normalizeMarks(text: string, marks: TextMark[] | undefined): TextMark[] {
  return fromChars(text, toChars(text, marks));
}

/**
 * `patch` written over the characters in [start, end).
 *
 * A key the patch leaves `undefined` is left alone, so applying `{bold: true}`
 * keeps the colour already on those words. An `align` reaches every line the
 * range touches, whole, since a line has one alignment.
 */
export function applyMark(
  value: RichText,
  start: number,
  end: number,
  {align, ...patch}: TextMarkStyle,
): TextMark[] {
  const {text} = value;
  const chars = toChars(text, value.marks);
  const from = Math.max(0, Math.min(start, end));
  const to = Math.min(text.length, Math.max(start, end));
  for (let i = from; i < to; i++) chars[i] = cleanStyle({...chars[i], ...patch});
  if (align !== undefined) {
    for (const [lineStart, lineEnd] of lineSpans(text)) {
      if (lineEnd <= from || lineStart >= Math.max(to, from + 1)) continue;
      for (let i = lineStart; i < lineEnd; i++) chars[i] = cleanStyle({...chars[i], align});
    }
  }
  return fromChars(text, chars);
}

/**
 * The marks of `oldText`, carried onto `newText` after a plain-text edit.
 *
 * The edit is read as one replaced span: whatever the two strings share at the
 * start and at the end keeps its style, and the characters typed in between
 * take the style of the character before them, the way a word processor
 * continues the run the caret sits in. Typing at the very start takes the style
 * of the first character instead, since there is nothing before it.
 *
 * This is what lets any plain editor — the side panel's label field, a variable
 * appended from the toolbar — change the text without knowing marks exist.
 */
export function rebaseMarks(
  oldText: string,
  marks: TextMark[] | undefined,
  newText: string,
): TextMark[] {
  if (!marks || marks.length === 0) return [];
  if (oldText === newText) return normalizeMarks(newText, marks);
  const old = toChars(oldText, marks);
  const max = Math.min(oldText.length, newText.length);
  let prefix = 0;
  while (prefix < max && oldText[prefix] === newText[prefix]) prefix++;
  let suffix = 0;
  while (
    suffix < max - prefix &&
    oldText[oldText.length - 1 - suffix] === newText[newText.length - 1 - suffix]
  ) {
    suffix++;
  }
  const insertedLength = newText.length - prefix - suffix;
  const inherit = prefix > 0 ? old[prefix - 1] : old[prefix];
  const chars = [
    ...old.slice(0, prefix),
    ...Array.from({length: insertedLength}, () => inherit),
    ...old.slice(oldText.length - suffix),
  ];
  return fromChars(newText, chars);
}

/** The text between `from` and `to`, with the marks that fall inside it. */
function sliceRichText(value: RichText, from: number, to: number): RichText {
  const text = value.text.slice(from, to);
  return {text, marks: fromChars(text, toChars(value.text, value.marks).slice(from, to))};
}

/** Leading and trailing whitespace removed, the marks shifted with the text. */
export function trimRichText(value: RichText): RichText {
  const start = value.text.length - value.text.trimStart().length;
  const end = value.text.trimEnd().length;
  return end <= start ? {text: '', marks: []} : sliceRichText(value, start, end);
}

/** The text cut into runs that each draw in one style, for a renderer. */
export function richSegments(value: RichText): TextSegment[] {
  const marks = normalizeMarks(value.text, value.marks);
  if (marks.length === 0) return value.text ? [{text: value.text}] : [];
  const out: TextSegment[] = [];
  let at = 0;
  for (const m of marks) {
    if (m.start > at) out.push({text: value.text.slice(at, m.start)});
    out.push({text: value.text.slice(m.start, m.end), style: cleanStyle(m)});
    at = m.end;
  }
  if (at < value.text.length) out.push({text: value.text.slice(at)});
  return out;
}

/**
 * The text cut into lines that each carry their alignment, for a renderer; or
 * `null` when no line has one of its own, so the text draws inline as it
 * always has. A text ending in `\n` ends with an empty line, which an editor
 * needs to put the caret on.
 */
export function alignedLines(value: RichText): TextLine[] | null {
  const chars = toChars(value.text, normalizeMarks(value.text, value.marks));
  if (!chars.some((c) => c?.align)) return null;
  const lines: TextLine[] = lineSpans(value.text).map(([start, end]) => ({
    align: chars[end - 1]?.align,
    segments: richSegments(sliceRichText(value, start, end)),
  }));
  if (value.text.endsWith('\n')) lines.push({segments: []});
  return lines;
}

/**
 * How the toolbar reads [start, end) of `value`, over the `base` its text
 * inherits from the element.
 *
 * Bold shows pressed only when every selected character draws bold, the way
 * every editor does it, so a press on a mixed selection makes all of it bold. A
 * colour, size or alignment the selection does not share reads as the element's.
 */
export function selectionFormat(
  value: RichText,
  start: number,
  end: number,
  base: TextFormat,
): TextFormat {
  const chars = toChars(value.text, value.marks)
    .slice(Math.min(start, end), Math.max(start, end))
    .map((style): TextFormat => ({...base, ...style}));
  const all = (k: 'bold' | 'italic' | 'underline') => chars.every((c) => c[k]);
  const shared = <K extends 'color' | 'font_size' | 'align'>(k: K): TextFormat[K] => {
    const first = chars[0]?.[k] ?? base[k];
    return chars.every((c) => c[k] === first) ? first : base[k];
  };
  return {
    bold: all('bold'),
    italic: all('italic'),
    underline: all('underline'),
    color: shared('color'),
    font_size: shared('font_size'),
    align: shared('align'),
  };
}

/** Marks for storage: `undefined` rather than an empty list, so a template with
 *  none saves the same JSON it always did. */
export function storedMarks(text: string, marks: TextMark[] | undefined): TextMark[] | undefined {
  const normal = normalizeMarks(text, marks);
  return normal.length > 0 ? normal : undefined;
}
